import { Liquid } from 'liquidjs'
import { safeLiquidRender } from '@rightmessage/shared-utils'
import { Dimension } from '@/types/config'

const engine = new Liquid()

export const cleanupLiquid = (email: string) => {
  email = email || ''
  // Normalize stray dash placements
  email = email.replace(/{-%/g, '{%-')
  email = email.replace(/%-}/g, '-%}')
  email = email.replace(/-%>/g, '-%}')

  // For preview rendering: trim whitespace around control tags to avoid
  // extra blank lines when tags stand alone on lines.
  // Convert `{% if ... %}`, `{% elsif ... %}`, `{% else %}`, `{% endif %}` to `{%- ... -%}`.
  email = email.replace(/\{%\s*(if|elsif|else|endif)([^%]*?)%\}/g, (_m, kw, rest) => `{%- ${kw}${rest} -%}`)

  return email
}

export const personalizeEmailWithLiquid = (email: string, data: any) => {
  email = cleanupLiquid(email)

  // Insert comment markers around conditionals, render, then convert markers to spans
  const startMarker = (token: string, value: string) => `<!--RM-START token="${token}" value="${value}"-->`
  const endMarker = `<!--RM-END-->`

  const blockRegex = /({%-?\s*if\s+([a-zA-Z][a-zA-Z0-9_]*(?:\.[a-zA-Z][a-zA-Z0-9_]*)*)\s*(?:==|contains)\s*"([^"]*)"[\s\S]*?{%-?\s*endif\s*-?%})/g

  const marked = email.replace(blockRegex, (_m, full, token, value) => {
    return `${startMarker(token.trim(), value.trim())}${full}${endMarker}`
  })

  let rendered = safeLiquidRender(marked, data, engine)

  // Convert markers into highlight wrappers, choosing a block-safe <div>
  // when the highlighted content contains block-level tags; otherwise <span>.
  const startRe = /<!--RM-START\s+token=\"([^\"]+)\"\s+value=\"([^\"]+)\"-->/g
  const endMarkerStr = '<!--RM-END-->'
  const blockTagRegex = /<(p|h1|h2|h3|h4|h5|h6|ul|ol|li|div|blockquote|pre|table|tr|td|th)\b/i

  let result = ''
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = startRe.exec(rendered)) !== null) {
    const startIdx = match.index
    const token = match[1]
    const value = match[2]

    // Append any text before this marker
    result += rendered.slice(lastIndex, startIdx)

    // Find matching end marker
    const afterStart = startRe.lastIndex
    const endIdx = rendered.indexOf(endMarkerStr, afterStart)
    if (endIdx === -1) {
      // No end marker; append rest and stop
      result += rendered.slice(startIdx)
      lastIndex = rendered.length
      break
    }

    const inner = rendered.slice(afterStart, endIdx)
    const useDiv = blockTagRegex.test(inner)
    const tag = useDiv ? 'div' : 'span'
    const open = `<${tag} class=\"email-liquid-highlight\" data-token-id=\"${token}\" data-token-value=\"${value}\">`
    const close = `</${tag}>`

    result += open + inner + close

    // Move past the end marker
    lastIndex = endIdx + endMarkerStr.length
  }

  // Append any remaining tail
  if (lastIndex < rendered.length) {
    result += rendered.slice(lastIndex)
  }

  rendered = result || rendered

  return rendered
}

export const segmentCombinationsPossible = (segmentGroups: Dimension[]) => {
  const dimensions = segmentGroups.map((sg) => ({
    dimensionId: sg.id,
    segments: sg.segments.map((s) => s.id)
  }))

  function combine(
    index: number,
    current: Array<{ dimensionId: string; segmentId: string | null }>
  ) {
    // Base case: if we've processed all dimensions, return the current combination
    if (index === dimensions.length) {
      // Create a unique ID by joining dimension and segment IDs
      const id = current
        .map(
          ({ dimensionId, segmentId }) =>
            `${dimensionId}:${segmentId || 'null'}`
        )
        .join('_')
      return [{ id, segments: current }]
    }

    const result = []
    const currentDimension = dimensions[index]

    // For each possible segment in the current dimension (including null)
    for (const segmentId of currentDimension.segments) {
      result.push(
        ...combine(index + 1, [
          ...current,
          {
            dimensionId: currentDimension.dimensionId,
            segmentId
          }
        ])
      )
    }

    return result
  }

  return combine(0, [])
}
